import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Eye, KeyRound, ShieldCheck, UserCog, UserPlus, UserX } from 'lucide-react';
import { admin } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import SupportAccessDialog from '../components/SupportAccessDialog';

const roleLabel = role => role === 'super_admin' ? 'Super Admin' : 'Manager';

const AdminTeam = () => {
  const { user } = useAuth();
  const [supportTarget, setSupportTarget] = useState(null);
  const [staff, setStaff] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candidateId, setCandidateId] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const response = await admin.getStaff();
      setStaff(response.data.staff);
      setCandidates(response.data.candidates);
      setCandidateId(current => response.data.candidates.some(item => item.id === current) ? current : '');
    } catch (error) {
      console.error('Admin access error:', error);
      toast.error(error.response?.data?.error || 'Failed to load admin access');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRole = async (member, role) => {
    if (role === 'user' && !window.confirm(`Remove admin access for ${member.name}? They will return to a standard user account.`)) return;
    setSavingId(member.id);
    try {
      const response = await admin.setStaffRole(member.id, role);
      toast.success(response.data.message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update admin access');
    } finally {
      setSavingId(null);
    }
  };

  const promoteCandidate = async () => {
    const candidate = candidates.find(item => item.id === candidateId);
    if (!candidate) return;
    if (!window.confirm(`Give ${candidate.name} Manager access? Their current session will be signed out.`)) return;
    setSavingId(candidate.id);
    try {
      const response = await admin.setStaffRole(candidate.id, 'manager');
      toast.success(response.data.message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to grant Manager access');
    } finally {
      setSavingId(null);
    }
  };

  const toggleStatus = async member => {
    const nextStatus = !member.isActive;
    if (!nextStatus && !window.confirm(`Suspend ${member.name}'s admin access? Their current session will end.`)) return;
    setSavingId(member.id);
    try {
      const response = await admin.setStaffStatus(member.id, nextStatus);
      toast.success(response.data.message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update admin status');
    } finally {
      setSavingId(null);
    }
  };

  const sendReset = async member => {
    setSavingId(member.id);
    try {
      await admin.sendStaffPasswordReset(member.id);
      toast.success(`Password reset sent to ${member.email}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send password reset');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500/30 border-t-primary-500" /></div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <ShieldCheck className="text-primary-600" size={30} />
          <h1 className="font-display text-3xl font-bold text-gray-900">Admin Access</h1>
        </div>
        <p className="mt-2 max-w-3xl text-gray-600">Super Admins can manage the whole Hub. Managers can manage, report on, and receive alerts for the users assigned to them.</p>
      </div>

      <section className="card mb-6 p-5">
        <div className="mb-4 flex items-center gap-2">
          <UserPlus size={19} className="text-primary-600" />
          <div><h2 className="font-semibold text-gray-900">Grant Manager access</h2><p className="text-xs text-gray-500">Promote an existing active user account</p></div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select value={candidateId} onChange={event => setCandidateId(event.target.value)} className="input-field flex-1">
            <option value="">Choose an existing user…</option>
            {candidates.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.name} — {candidate.email}</option>)}
          </select>
          <button type="button" disabled={!candidateId || savingId === candidateId} onClick={promoteCandidate} className="btn-primary disabled:cursor-not-allowed disabled:opacity-50">Make Manager</button>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4"><h2 className="font-semibold text-gray-900">Administrators</h2><p className="text-xs text-gray-500">Role and access changes sign the affected person out immediately</p></div>
        <div className="divide-y divide-gray-100">
          {staff.map(member => (
            <div key={member.id} className="p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-gray-900">{member.name}</p>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${member.adminRole === 'super_admin' ? 'bg-primary-100 text-primary-700' : 'bg-blue-100 text-blue-700'}`}>{roleLabel(member.adminRole)}</span>
                    {!member.isActive && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Suspended</span>}
                    {member.isCurrentUser && <span className="text-xs font-medium text-gray-400">You</span>}
                  </div>
                  <p className="truncate text-sm text-gray-500">{member.email}</p>
                  <p className="mt-1 text-xs text-gray-500">{member.userCount} assigned user{member.userCount === 1 ? '' : 's'} · Last login {member.lastLogin ? new Date(member.lastLogin).toLocaleDateString() : 'never'}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {user?.canViewAs && member.adminRole === 'manager' && !member.isCurrentUser && <button type="button" disabled={!member.isActive} onClick={() => setSupportTarget({ id: member.id, name: member.name, email: member.email })} className="btn-primary inline-flex items-center gap-2 px-3 py-2 text-sm disabled:opacity-50"><Eye size={16} />View as manager</button>}
                  <label className="flex items-center gap-2 text-sm text-gray-600"><UserCog size={16} /> Role
                    <select value={member.adminRole} disabled={member.isCurrentUser || savingId === member.id} onChange={event => updateRole(member, event.target.value)} className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm disabled:bg-gray-100">
                      <option value="manager">Manager</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </label>
                  <button type="button" disabled={savingId === member.id} onClick={() => sendReset(member)} className="btn-outline inline-flex items-center gap-2 px-3 py-2 text-sm"><KeyRound size={16} /> Reset password</button>
                  {!member.isCurrentUser && <button type="button" disabled={savingId === member.id} onClick={() => toggleStatus(member)} className="btn-outline inline-flex items-center gap-2 px-3 py-2 text-sm">{member.isActive ? <UserX size={16} /> : <ShieldCheck size={16} />}{member.isActive ? 'Suspend' : 'Reactivate'}</button>}
                  {!member.isCurrentUser && <button type="button" disabled={savingId === member.id || member.userCount > 0} onClick={() => updateRole(member, 'user')} title={member.userCount > 0 ? 'Reassign this manager’s users first' : 'Remove admin access'} className="btn-danger-ghost inline-flex items-center gap-2 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"><UserX size={16} /> Remove access</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      {supportTarget && <SupportAccessDialog target={supportTarget} onClose={() => setSupportTarget(null)} />}
    </div>
  );
};

export default AdminTeam;
